import { Box, Card, CardActionArea, Stack, Typography } from "@mui/material";
import { crDateOf } from "@despejo/domain";
import { overduePenalty } from "@despejo/compliance-engine";
import { DeadlineText, FilingChip, MoneyText, PaymentChip, StatusChip, formatCRCText } from "@despejo/ui";
import { usePenaltyParams } from "../lib/queries.js";
import { periodText } from "../lib/dashboard.js";
import type { ObligationRow } from "../lib/types.js";

const TIER_LABEL: Record<string, string> = {
  TRACK: "Solo seguimiento",
  CHECKLIST: "Guiada",
  CALCULATED: "Calculada",
  RECONCILED: "Conciliada",
};

export function ObligationCard({ row, onOpen, showTaxpayer = true }: { row: ObligationRow; onOpen: (row: ObligationRow) => void; showTaxpayer?: boolean }) {
  const overdue = row.is_overdue_filing || row.is_overdue_payment;
  const params = usePenaltyParams();
  const pen = overdue && params.data ? overduePenalty(params.data, row, crDateOf(new Date())) : null;
  return (
    <Card sx={overdue ? { borderColor: "error.main", borderWidth: 2 } : undefined}>
      <CardActionArea onClick={() => onOpen(row)} sx={{ p: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ justifyContent: "space-between" }}>
          <Box sx={{ minWidth: 0 }}>
            {showTaxpayer && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {row.taxpayer_name}
              </Typography>
            )}
            <Typography variant="h6" component="h3">
              {row.name_es}
              {row.form ? ` (${row.form})` : ""} — {periodText(row.period_key)}
            </Typography>
            <DeadlineText date={row.due_date} overdue={overdue} />
            {pen && (
              <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                Sanción estimada si se resuelve hoy: {formatCRCText(pen.exposure.toFixed(2))}
                {pen.amountKnown ? "" : " (sin monto: solo la multa)"} · con regularización voluntaria: {formatCRCText(pen.withVoluntaryReduction.toFixed(2))}
              </Typography>
            )}
          </Box>
          <Stack spacing={1} sx={{ alignItems: { sm: "flex-end" }, flexShrink: 0 }}>
            <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap", justifyContent: { sm: "flex-end" } }}>
              {overdue && <StatusChip tone="error" icon="error" label="Vencida" />}
              {row.provisional && <StatusChip tone="warning" icon="help" label="Fecha por confirmar" />}
              {row.has_filing !== false && <FilingChip status={row.filing_status} />}
              {row.has_payment && <PaymentChip status={row.payment_status} />}
            </Stack>
            <Stack direction="row" spacing={1} sx={{ alignItems: "baseline" }}>
              <Typography variant="caption" color="text.secondary">
                {TIER_LABEL[row.support_tier] ?? row.support_tier}
              </Typography>
              {row.has_payment && <MoneyText amount={row.amount_due ?? row.expected_amount} variant="body2" />}
            </Stack>
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
}
